import { LngLatLike, LngLat } from 'maplibre-gl'
import config from '@src/config'
import axios from 'axios'
import { AxiosRequestConfig } from 'axios'
import { TravelMode, GeometryType, AvoidType, OverviewType, ApproachType } from '@src/common/interface'
import { NBRequestStatus, StepIntersections, StepManeuver } from './common'

export type DirectionsRouteType = 'fastest' | 'shortest'
export type DirectionsRoadInfo = 'max_speed'
export type DirectionsHazmatType = 'general' | 'circumstantial' | 'explosive' | 'harmfulToWater'

/**
 * A directions query to be sent to the DirectionsSerivce
 */
export type DirectionsRequest = {
  /** The starting point of a route. */
  origin: LngLatLike
  /** The ending point of a route. */
  destination: LngLatLike
  /** Coordinates along the route between the Origin and the Destination. */
  waypoints?: LngLatLike[]
  /** A parameter which which sets the transportation mode for the route. PS: Only the 4w/car profile is enabled by default. The other customised routing profiles are only supported in Premium plan. */
  mode?: TravelMode | string
  /** The steps flag determines whether to return the individual maneuver instructions that comprise the route in the response. */
  steps?: boolean
  /** This parameter sets the departure time in Unix epoch time seconds format. Unit: seconds. PS: This parameter is only supported in Premium plan. */
  departureTime?: number
  /** Unique session ID. If set, the response will reuse cached trip characteristics. PS: This parameter is only supported in Premium plan. */
  session?: string
  /** Sets the output format of the route geometry in the response. Default: GeometryType.POLYLINE6. */
  geometry?: GeometryType
  /** If enabled, the API will return alternative routes. You can set the number of routes in the altcount property. */
  alternatives?: boolean
  /** Sets the number of alternative routes to return. Default: 3. */
  altCount?: number
  /** Setting this will ensure the route avoids ferry, tolls or highways. */
  avoid?: AvoidType[]
  /** Output verbosity of overview (whole trip) geometry. Default: full. */
  overview?: OverviewType
  /** A list indicating the side of the road from which to approach waypoints in a requested route. If provided, the number of approaches must be one more than the number of waypoints. */
  approaches?: ApproachType[]
  /** Use this option to switch to 6w or truck specific routing. Using this option enables specific settings available only for truck based routing. Accepts `flexible`. */
  option?: string
  /**
   * api key for the request
   */
  apiKey?: string
  /**
   * This defines the dimensions of a truck in centimeters (cm). This parameter is effective only when the mode=truck. Maximum dimensions are as follows:
   * Height = 1000 cm
   * Width = 5000 cm
   * Length = 5000 cm
   */
  truckSize?: number[]
  /**
   * This defines the weight of the truck in kilograms (kg). This parameter is effective only when the mode=`truck`. Maximum weight is 100000 kg.
   */
  truckWeight?: number
  /** Set the route type that needs to be returned. */
  routeType?: DirectionsRouteType
  /** Use this parameter to receive segment-wise maximum speed information of the route in the response. `max_speed is` the only allowed value. */
  roadInfo?: DirectionsRoadInfo[]
  /**
   * Specify if crossing an international border is expected. When set to false, the API will place a penalty for crossing an international border through a checkpoint. Consequently, alternative routes will be preferred if they are feasible for the given set of `destination` or `waypoints` . A higher penalty is placed on a route crossing international border without a checkpoint, hence such routes will be preferred the least.
   *
   * When set to true, there will be no penalty for crossing an international border.
   *
   * This feature is available in North America and Singapore region only. Please get in touch with support@nextbillion.ai to enquire/enable other areas.
   */
  crossBorder?: boolean
  /**
   * Specify the total load per axle (including the weight of trailers and shipped goods) of the truck, in tonnes. When used, the service will return routes which are legally allowed to carry the load specified per axle.
   *
   * Please note this parameter is effective only when `mode=truck`.
   */
  truckAxleLoad?: number
  /**
   * Specify the type of hazardous material being carried and the service will avoid roads which are not suitable for the type of goods specified.
   *
   * Allowed Values: general circumstantial explosive harmfulToWater
   *
   * Please note that this parameter is effective only when `mode=truck`.
   */
  hazmatType?: DirectionsHazmatType[]
}

interface NormalizedDirectionsRequest extends DirectionsRequest {
  /** The starting point of a route. */
  origin: LngLat
  /** The ending point of a route. */
  destination: LngLat
  /** Coordinates along the route between the Origin and the Destination. */
  waypoints?: LngLat[]
}

/**
 * The directions response retrieved from the directions server.
 */
export type DirectionsResult = {
  country_code?: string
  /** A string indicating the state of the response. This is a separate code than the HTTP status code. On normal valid responses, the value will be Ok. */
  status: NBRequestStatus | string
  /** error message if status is not 200. */
  msg?: string
  /** routes calculated. */
  routes?: Array<DirectionsResponseRoute>
}

/**
 * @interface DirectionsResponseRoute
 * The route information returned by the directions service.
 */
export interface DirectionsResponseRoute {
  distance: number
  distance_full?: number
  raw_duration?: number
  duration: number
  predicted_duration?: number
  /** encoded geometry value for step in polyline or polyline6. */
  geometry?: string
  start_location?: {
    latitude: number
    longitude: number
  }
  end_location?: {
    latitude: number
    longitude: number
  }
  geojson?: {
    coordinates: Array<Array<number>>
  }
  legs: Array<DirectionsResponseLeg>
  special_objects?: any
}

/**
 * @interface DirectionsResponseLeg
 * The leg information in the route.
 */
export interface DirectionsResponseLeg {
  distance: {
    value: number
  }
  raw_duration?: {
    value: number
  }
  duration: {
    value: number
  }
  start_location?: {
    latitude: number
    longitude: number
  }
  end_location?: {
    latitude: number
    longitude: number
  }
  steps: Array<DirectionsResponseLegStep>
}

/**
 * @interface DirectionsResponseLegStep
 * The step information in the leg.
 */
export interface DirectionsResponseLegStep {
  name?: string
  reference?: string
  geometry?: string
  distance: {
    value: number
  }
  geojson?: {
    coordinates: Array<Array<number>>
  }
  intersections?: Array<StepIntersections>
  maneuver?: StepManeuver
  duration: {
    value: number
  }
  start_location: {
    latitude: number
    longitude: number
  }
  end_location: {
    latitude: number
    longitude: number
  }
}

function checkAndnormalizeOpt(opt: DirectionsRequest): NormalizedDirectionsRequest {
  if (!opt.origin) {
    throw new Error('origin is required in DirectionsRequest')
  }

  if (!opt.destination) {
    throw new Error('destination is required in DirectionsRequest')
  }

  const nOpt: NormalizedDirectionsRequest = Object.assign({}, opt, {
    origin: LngLat.convert(opt.origin),
    destination: LngLat.convert(opt.destination),
    waypoints: opt.waypoints ? opt.waypoints.map((w) => LngLat.convert(w)) : opt.waypoints,
  }) as NormalizedDirectionsRequest

  if (nOpt.approaches) {
    if (nOpt.waypoints) {
      if (nOpt.waypoints.length + 1 !== nOpt.approaches.length) {
        throw new Error('The number of approaches must be one more than the number of waypoints.')
      }
    } else {
      if (nOpt.approaches.length !== 1) {
        throw new Error('The number of approaches must be one more than the number of waypoints. ')
      }
    }
  }
  return nOpt
}

/**
 * A service for calculating directions between locations.
 * Can be accessed by `nextbillion.maps.DirectionsService` if you load SDK by CDN url.
 */
export class DirectionsService {
  /**
   * Request directions between an origin and a destination.
   * @param opt request options
   * @returns directions result of the request
   */
  async route(opt: DirectionsRequest): Promise<DirectionsResult | null> {
    if (!config.API_KEY && !opt.apiKey) {
      throw new Error('In order to call the NextBillion.ai API, you must provide a valid API Key.')
    }

    if (!config.API_HOST) {
      throw new Error('An API host is required to make request to NextBillion.ai API.')
    }

    const nOpt = checkAndnormalizeOpt(opt)

    const params: any = {
      origin: `${nOpt.origin.lat},${nOpt.origin.lng}`,
      destination: `${nOpt.destination.lat},${nOpt.destination.lng}`,
      steps: nOpt.steps || 'false',
      alternatives: nOpt.alternatives || 'false',
      departure_time: nOpt.departureTime || 0,
      session: nOpt.session || '',
      geometry: nOpt.geometry || 'polyline6',
    }

    if (Array.isArray(nOpt.waypoints)) {
      const waypoints = nOpt.waypoints.map((point) => `${point.lat},${point.lng}`).join('|')
      params['waypoints'] = waypoints
    }
    if (nOpt.altCount) {
      params.altcount = nOpt.altCount
    }
    if (nOpt.mode) {
      params.mode = nOpt.mode
    }
    if (nOpt.session) {
      params.session = nOpt.session
    }
    if (nOpt.geometry) {
      params.geometry = nOpt.geometry
    }
    if (nOpt.avoid) {
      params.avoid = nOpt.avoid.join('|')
    }
    if (nOpt.overview) {
      params.overview = nOpt.overview
    }
    if (nOpt.approaches) {
      params.approaches = nOpt.approaches.join(';')
    }
    if (nOpt.option) {
      params.option = nOpt.option
    }
    if (nOpt.truckSize) {
      params.truck_size = nOpt.truckSize.join(',')
    }
    if (nOpt.truckWeight) {
      params.truck_weight = nOpt.truckWeight
    }
    if (nOpt.routeType) {
      params.route_type = nOpt.routeType
    }
    if (nOpt.roadInfo) {
      params.road_info = nOpt.roadInfo.join('')
    }
    if (nOpt.crossBorder) {
      params.cross_border = nOpt.crossBorder
    }
    if (nOpt.truckAxleLoad) {
      params.truck_axle_load = nOpt.truckAxleLoad
    }
    if (nOpt.hazmatType) {
      params.hazmat_type = nOpt.hazmatType.join('|')
    }

    const reqConfig: AxiosRequestConfig = {
      params,
    }
    if (config.API_KEY_HEADER_NAME) {
      reqConfig.headers = {
        [config.API_KEY_HEADER_NAME]: nOpt.apiKey || config.API_KEY,
      }
    } else {
      params.key = nOpt.apiKey || config.API_KEY
    }

    return axios
      .get(`https://${config.API_HOST}/directions/json`, reqConfig)
      .then((resp) => resp.data as DirectionsResult)
      .catch((err) => {
        if (err.response && err.response.data) {
          return err.response.data as DirectionsResult
        } else {
          return err
        }
      })
  }
}
